import { useState } from 'react';

import Button from 'honeydew/Button';
import { LoadingIcon } from 'honeydew/Loader';
import { formatDateString, localizeTimeZone } from 'utils/dateUtils';
import twclsx from 'utils/twclsx';

/**
 * Displays the SNAP-eligible total amount for EBT payment flow
 * @param {string} className - Additional CSS classes
 * @param {number|string} total - The SNAP-eligible total amount
 */
function SnapEligibleTotal({ className, total }) {
    const formattedTotal = Number(total)?.toFixed(2);

    return (
        <p
            data-testid="ebt-snap-eligible-total"
            className={twclsx('text-body-md flex items-center justify-between gap-10', className)}
        >
            SNAP-Eligible Total <span className="font-grotesk-bold">${formattedTotal}</span>
        </p>
    );
}

/**
 * Displays the current amount being processed in the EBT payment flow
 * @param {number|string} amount - The current amount
 * @param {string} className - Additional CSS classes
 */
function CurrentAmount({ amount, className }) {
    const formattedAmount = Number(amount)?.toFixed(2);

    return (
        <p
            className={twclsx('text-body-md flex items-center justify-between gap-10', className)}
            data-testid="ebt-current-amount"
        >
            Current EBT Amount <span className="text-brand-primary font-grotesk-bold">${formattedAmount}</span>
        </p>
    );
}

/**
 * Button component for EBT payment flow steps with loading state support
 * @param {React.ReactNode} children - Button content
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Whether the button is disabled
 * @param {boolean} loading - Whether the button is in loading state
 * @param {string} loadingText - Text to show when loading
 * @param {Function} onClick - Click handler function
 */
function StepButton({ children, className, disabled, loading, loadingText, onClick }) {
    return (
        <div className={twclsx('mt-20', className)} data-testid="ebt-step-button">
            <Button onClick={onClick} disabled={disabled} type="submit">
                {loading ? (
                    <span className="flex items-center gap-10">
                        <LoadingIcon />
                        <span>{loadingText || 'Processing...'}</span>
                    </span>
                ) : (
                    children
                )}
            </Button>
        </div>
    );
}

/**
 * Card container for EBT payment flow step content
 * @param {React.ReactNode} children - Card content
 * @param {string} className - Additional CSS classes
 */
function StepCard({ children, className }) {
    return (
        <div className={twclsx('rounded-lg bg-off-white p-20', className)} data-testid="ebt-step-card">
            {children}
        </div>
    );
}

/**
 * Heading component for EBT payment flow steps
 * @param {React.ReactNode} children - Heading content
 * @param {string} className - Additional CSS classes
 */
function StepHeading({ children, className }) {
    return (
        <h2
            className={twclsx('font-grotesk-bold text-body-desktop-lg md:text-heading-desktop-xs', className)}
            data-testid="ebt-step-heading"
        >
            {children}
        </h2>
    );
}

/**
 * Displays information about credit card charge date for remaining order amount
 * @param {string} className - Additional CSS classes
 * @param {string} marketCloseDate - The date when the shopping window closes
 * @param {string} orderChargeDate - The date when the credit card will be charged
 */
function CreditCardCharge({ className, marketCloseDate, orderChargeDate }) {
    if (!orderChargeDate) return null;

    return (
        <p className={twclsx('text-body-md text-black', className)}>
            After your shopping window closes on {formatDateString(marketCloseDate, true, false, false, true)} at{' '}
            {localizeTimeZone(marketCloseDate)}, the remainder of your order total will be charged to your default
            payment method on {formatDateString(orderChargeDate, true, false, false, true)}.
        </p>
    );
}

/**
 * Success icon with animated checkbox for EBT payment flow completion
 * @param {number} height - Icon height in pixels
 * @param {number} width - Icon width in pixels
 */
function SuccessIcon({ height = 40, width = 40 }) {
    // TODO: need an IF version?
    const baseUrl = 'https://img-cdn.misfitsmarket.com/boysenberry/icons/animated-checkbox-no-loop.gif';

    // Force GIF to reload on mount by adding cache-busting query parameter
    // This ensures the animation plays even if the browser cached the final frame
    const [imageSrc] = useState(() => `${baseUrl}?t=${Date.now()}`);

    return <img src={imageSrc} width={width} height={height} alt="success" aria-hidden="true" />;
}

export { CurrentAmount, SnapEligibleTotal, StepButton, StepCard, StepHeading, CreditCardCharge, SuccessIcon };
